import { pickClass, setActiveClass } from "@/lib/activeClass";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronRight, IdCard, TrendingUp, TrendingDown, Minus, CalendarX, Sparkles, Info, ArrowLeft, QrCode, Share2, Download, Copy, Printer, ExternalLink, Camera, ImageIcon, Trash2, RefreshCw, XCircle, Users } from "lucide-react";
import { toast } from "sonner";

const sortAlpha = (a, b) =>
  (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" }) ||
  (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" });

export default function StudentFiches() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      if (data.length > 0) setClassId(pickClass(data));
    });
  }, []);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    api.get("/students", { params: { class_id: classId } })
      .then(({ data }) => setStudents([...data].sort(sortAlpha)))
      .catch((e) => toast.error(formatError(e)));
  }, [classId]);

  useEffect(() => {
    if (!selectedId) { setFiche(null); return; }
    setLoading(true);
    setFiche(null);
    api.get(`/students/${selectedId}/fiche`)
      .then(({ data }) => {
        setFiche(data);
        // On mobile, scroll to top of detail panel
        if (window.matchMedia("(max-width: 767px)").matches) {
          setTimeout(() => {
            document.getElementById("fiche-detail")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 50);
        }
      })
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return students;
    return students.filter((st) => `${st.prenom} ${st.nom}`.toLowerCase().includes(s));
  }, [students, search]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <IdCard className="w-7 h-7 text-[#1F7A63]" /> Fiches élèves
        </h1>
        <p className="text-sm text-[#5C736A]">Informations détaillées, notes, absences et indice de performance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Left: student list — hidden on mobile when a fiche is open */}
        <div className={`ek-card p-3 space-y-3 h-fit ${selectedId ? "hidden md:block" : ""}`}>
          <Select value={classId} onValueChange={(v) => { setClassId(v); setSelectedId(null); setActiveClass(v); }}>
            <SelectTrigger data-testid="fiche-class-select"><SelectValue placeholder="Classe" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
            <Input className="pl-9" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#E2E8F0] -mx-3">
            {filtered.map((st) => (
              <button
                key={st.id}
                onClick={() => setSelectedId(st.id)}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-2 ${selectedId === st.id ? "bg-[#E8F2EF]" : ""}`}
                data-testid={`fiche-select-${st.matricule}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{st.prenom} <span className="text-[#5C736A] font-normal">{st.nom}</span></div>
                  <div className="text-xs text-[#5C736A]">{st.matricule}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#5C736A] shrink-0" />
              </button>
            ))}
            {filtered.length === 0 && <div className="p-4 text-sm text-[#5C736A] text-center">Aucun élève.</div>}
          </div>
        </div>

        {/* Right: detail — full width on mobile when opened */}
        <div id="fiche-detail" className={selectedId ? "" : "hidden md:block"}>
          {!selectedId && (
            <div className="ek-card p-10 text-center text-sm text-[#5C736A]">
              Sélectionnez un élève dans la liste pour voir sa fiche.
            </div>
          )}
          {selectedId && (
            <>
              {/* Back button — mobile only */}
              <div className="md:hidden mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedId(null)}
                  className="gap-1"
                  data-testid="fiche-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Retour à la liste
                </Button>
              </div>
              {loading && <div className="skel h-64" />}
              {!loading && fiche && <FicheContent fiche={fiche} classId={classId} onReload={() => {
                // refresh both fiche and student list (in case name changed)
                setFiche(null);
                setLoading(true);
                api.get(`/students/${selectedId}/fiche`).then(({ data }) => setFiche(data))
                  .finally(() => setLoading(false));
              }} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FicheContent({ fiche, classId, onReload }) {
  const { student: st, class: cls, moyennes, evaluations, absences, evolution, indice, commentaire } = fiche;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const canManage = true; // route already protected

  const indiceColor = indice == null ? "#5C736A"
    : indice >= 8 ? "#1F7A63"
    : indice >= 6 ? "#4a9078"
    : indice >= 4 ? "#E6B800"
    : "#D93838";

  const cardActive = st.card_token_active !== false && !!st.card_token;
  const parentUrl = cardActive
    ? `${window.location.origin}/parent/card/${st.card_token}`
    : `${window.location.origin}/parent/${st.matricule}`;
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
  // Use token-based QR whenever available (secure); fallback to matricule URL
  const qrPngUrl = `${API_BASE}/parent/${st.matricule}/qr.png`;
  const fichePdfUrl = `${API_BASE}/parent/${st.matricule}/fiche.pdf`;
  const cardPdfUrl = `${API_BASE}/students/${st.id}/card.pdf`;
  const cardsSheetUrl = classId ? `${API_BASE}/students/cards.pdf?class_id=${classId}` : null;

  // ------ Photo handling (compress client-side) ------
  const compressToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Target portrait ~400×500 max
        const targetW = 400, targetH = 500;
        const ratio = Math.min(targetW / img.width, targetH / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPhoto = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Formats acceptés : JPG, JPEG, PNG, WEBP");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await compressToBase64(file);
      await api.post(`/students/${st.id}/photo`, { photo_base64: dataUrl });
      toast.success("Photo enregistrée");
      onReload && onReload();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Supprimer la photo de cet élève ?")) return;
    try {
      await api.delete(`/students/${st.id}/photo`);
      toast.success("Photo supprimée");
      onReload && onReload();
    } catch (e) { toast.error(formatError(e)); }
  };

  // ------ Card token actions ------
  const regenerateToken = async () => {
    if (!window.confirm("Régénérer le QR Code ? L'ancien lien deviendra invalide.")) return;
    setRegenBusy(true);
    try {
      await api.post(`/students/${st.id}/card-token/regenerate`);
      toast.success("QR Code régénéré");
      onReload && onReload();
    } catch (e) { toast.error(formatError(e)); }
    finally { setRegenBusy(false); }
  };
  const disableToken = async () => {
    if (!window.confirm("Désactiver le QR Code ? L'accès parent via QR sera bloqué.")) return;
    try {
      await api.delete(`/students/${st.id}/card-token`);
      toast.success("QR Code désactivé");
      onReload && onReload();
    } catch (e) { toast.error(formatError(e)); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(parentUrl); toast.success("Lien copié !"); }
    catch { toast.error("Impossible de copier"); }
  };
  const shareUrl = async () => {
    const shareData = {
      title: `Fiche de ${st.prenom} ${st.nom}`,
      text: `Consultez la fiche scolaire de ${st.prenom} ${st.nom} (matricule ${st.matricule}) sur EDUKATI.`,
      url: parentUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* user cancelled */ }
    }
    await copyUrl();
  };
  const downloadQr = async () => {
    try {
      const res = await fetch(qrPngUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr_${st.matricule}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("QR code téléchargé");
    } catch { toast.error("Erreur téléchargement"); }
  };
  const printQr = () => {
    const w = window.open("", "_blank", "width=400,height=500");
    if (!w) return;
    w.document.write(`
      <html><head><title>QR ${st.matricule}</title>
      <style>body{font-family:Arial,sans-serif;text-align:center;padding:20px}
      img{width:250px;height:250px}
      .mat{font-family:monospace;font-weight:bold;font-size:14px;margin-top:8px}
      .name{font-size:16px;margin-top:4px}
      @media print{@page{margin:5mm}}
      </style></head><body onload="window.print()">
        <img src="${qrPngUrl}" alt="QR" />
        <div class="mat">${st.matricule}</div>
        <div class="name">${st.prenom} ${st.nom}</div>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="ek-card p-5">
        <div className="flex items-start gap-4 flex-wrap">
          {/* Photo portrait (or initial fallback) */}
          <div className="shrink-0 relative">
            {st.photo_base64 ? (
              <img
                src={st.photo_base64}
                alt={`${st.prenom} ${st.nom}`}
                className="w-24 h-32 object-cover rounded-lg border-2 border-[#E8F2EF] shadow-sm"
                data-testid="student-photo-preview"
              />
            ) : (
              <div className="w-24 h-32 rounded-lg bg-[#E8F2EF] border-2 border-dashed border-[#1F7A63]/40 grid place-items-center">
                <div className="text-center">
                  <ImageIcon className="w-8 h-8 text-[#1F7A63]/60 mx-auto" />
                  <div className="text-[10px] text-[#5C736A] mt-1">Aucune photo</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
              {st.prenom} <span className="text-[#5C736A] font-normal">{st.nom}</span>
            </div>
            <div className="text-sm text-[#5C736A] mt-1">
              {st.matricule} · {st.sexe === "F" ? "Féminin" : "Masculin"} · {cls?.nom || "—"}
            </div>
            {/* Photo actions */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="user" className="hidden"
                onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="photo-camera-input" />
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden"
                onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="photo-file-input" />
              <Button size="sm" variant="outline" onClick={() => cameraInputRef.current?.click()} disabled={uploading} data-testid="photo-camera-btn">
                <Camera className="w-4 h-4 mr-1" /> Prendre une photo
              </Button>
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="photo-gallery-btn">
                <ImageIcon className="w-4 h-4 mr-1" /> Choisir dans la galerie
              </Button>
              {st.photo_base64 && (
                <Button size="sm" variant="outline" onClick={removePhoto} className="text-red-600 border-red-200" data-testid="photo-remove-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
              )}
              {uploading && <span className="text-xs text-[#5C736A] self-center">Compression…</span>}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[#5C736A] uppercase tracking-wider">Indice</div>
            <div className="text-4xl font-bold" style={{ fontFamily: "Outfit", color: indiceColor }}>
              {indice != null ? indice : "—"}<span className="text-lg text-[#5C736A]">/10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Carte scolaire — dedicated section */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <IdCard className="w-5 h-5 text-[#1F7A63]" /> Carte scolaire
        </h3>
        <p className="text-xs text-[#5C736A] mb-3">
          Format 85,6 × 54 mm · Photo, matricule, contacts et QR Code sécurisé vers l&apos;espace parent.
          {!cardActive && <span className="text-red-600 font-semibold"> · QR Code désactivé.</span>}
        </p>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <a href={cardPdfUrl} target="_blank" rel="noreferrer">
            <Button className="bg-[#1F7A63] hover:bg-[#155744] text-white" data-testid="card-download-btn">
              <Download className="w-4 h-4 mr-1" /> Télécharger la carte
            </Button>
          </a>
          {cardsSheetUrl && (
            <a href={cardsSheetUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="cards-sheet-btn">
                <Users className="w-4 h-4 mr-1" /> Toutes les cartes de la classe
              </Button>
            </a>
          )}
          <Button variant="outline" onClick={regenerateToken} disabled={regenBusy} data-testid="card-regen-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${regenBusy ? "animate-spin" : ""}`} /> Régénérer le QR
          </Button>
          {cardActive && (
            <Button variant="outline" onClick={disableToken} className="text-red-600 border-red-200" data-testid="card-disable-btn">
              <XCircle className="w-4 h-4 mr-1" /> Désactiver le QR
            </Button>
          )}
        </div>
      </div>

      {/* QR Code section — parent portal */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <QrCode className="w-5 h-5 text-[#1F7A63]" /> Accès parent
        </h3>

        {/* Primary CTA — always full-width & prominent (mobile-first) */}
        <a href={parentUrl} target="_blank" rel="noreferrer" data-testid="espace-eleve-btn" className="block mb-3">
          <Button className="w-full bg-[#1F7A63] hover:bg-[#155744] text-white gap-2 h-11">
            <ExternalLink className="w-4 h-4" /> Ouvrir l&apos;espace élève
          </Button>
        </a>

        <div className="flex items-start gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
          <img
            src={qrPngUrl}
            alt={`QR ${st.matricule}`}
            className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 border border-[#E2E8F0] rounded-lg p-1 bg-white mx-auto sm:mx-0"
            data-testid="qr-code-img"
          />
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-xs sm:text-sm text-[#1A2E28]">
              QR ou matricule <b className="font-mono text-[#1F7A63]">{st.matricule}</b> sur <code className="text-xs bg-slate-100 px-1 rounded whitespace-nowrap">/parent</code>.
            </p>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={shareUrl} data-testid="qr-share-btn" className="justify-start sm:justify-center">
                <Share2 className="w-4 h-4 mr-1.5" /> Partager
              </Button>
              <Button size="sm" variant="outline" onClick={downloadQr} data-testid="qr-download-btn" className="justify-start sm:justify-center">
                <Download className="w-4 h-4 mr-1.5" /> QR PNG
              </Button>
              <Button size="sm" variant="outline" onClick={printQr} data-testid="qr-print-btn" className="justify-start sm:justify-center">
                <Printer className="w-4 h-4 mr-1.5" /> Imprimer
              </Button>
              <Button size="sm" variant="outline" onClick={copyUrl} data-testid="qr-copy-btn" className="justify-start sm:justify-center">
                <Copy className="w-4 h-4 mr-1.5" /> Copier
              </Button>
              <a href={fichePdfUrl} target="_blank" rel="noreferrer" className="col-span-2 sm:col-span-1">
                <Button size="sm" variant="outline" data-testid="fiche-pdf-btn" className="w-full justify-start sm:justify-center">
                  <IdCard className="w-4 h-4 mr-1.5" /> Fiche PDF
                </Button>
              </a>
            </div>
            <p className="text-xs text-[#5C736A] hidden sm:block">
              💡 Partage = lien avec info · Impression / téléchargement = QR seul.
            </p>
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Informations personnelles</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <InfoRow label="Date de naissance" value={st.date_naissance} />
          <InfoRow label="Lieu de naissance" value={st.lieu_naissance} />
          <InfoRow label="Tuteur / Parent" value={st.tuteur} />
          <InfoRow label="N° responsable" value={st.telephone_parent} />
          <InfoRow label="Adresse" value={st.adresse} />
        </div>
      </div>

      {/* Moyennes */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          Notes & moyennes {evolution != null && (
            evolution > 0.5
              ? <span className="text-[#1F7A63] text-xs flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{evolution}</span>
              : evolution < -0.5
                ? <span className="text-red-600 text-xs flex items-center gap-1"><TrendingDown className="w-3 h-3" /> {evolution}</span>
                : <span className="text-[#5C736A] text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> stable</span>
          )}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MoyCard label="T1" value={moyennes.T1} />
          <MoyCard label="T2" value={moyennes.T2} />
          <MoyCard label="T3" value={moyennes.T3} />
          <MoyCard label="Annuelle" value={moyennes.annuelle} highlight />
        </div>
        {evaluations.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-[#5C736A] uppercase tracking-wider mb-2">Évaluations récentes</div>
            <div className="space-y-1.5">
              {evaluations.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-slate-50 rounded-lg">
                  <span>{e.session_nom}</span>
                  <span className="font-semibold">{e.total.toFixed(1)} / {e.bareme} <span className="text-[#5C736A] text-xs">({e.pct}%)</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Absences */}
      <div className="ek-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <CalendarX className="w-5 h-5 text-red-500" /> Absences
        </h3>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="text-xs text-[#5C736A]">Total</div>
            <div className="text-2xl font-bold">{absences.total}</div>
          </div>
          <div className="p-3 bg-[#E8F2EF]/50 rounded-lg">
            <div className="text-xs text-[#5C736A]">Justifiées</div>
            <div className="text-2xl font-bold text-[#1F7A63]">{absences.justifiees}</div>
          </div>
        </div>
      </div>

      {/* Commentaire */}
      <div className="ek-card p-5 bg-[#FCF8E6]/40 border-l-4 border-[#E6B800]">
        <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Sparkles className="w-5 h-5 text-[#E6B800]" /> Conseil pédagogique
        </h3>
        <ul className="space-y-1.5 text-sm text-[#1A2E28]">
          {commentaire.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#1F7A63] shrink-0 mt-0.5" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#5C736A] uppercase tracking-wider">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}

function MoyCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-lg text-center ${highlight ? "bg-[#E8F2EF] border border-[#1F7A63]/30" : "bg-slate-50"}`}>
      <div className="text-xs text-[#5C736A]">{label}</div>
      <div className={`text-xl font-bold ${highlight ? "text-[#1F7A63]" : ""}`} style={{ fontFamily: "Outfit" }}>
        {value != null ? value.toFixed(2) : "—"}<span className="text-xs text-[#5C736A]">/10</span>
      </div>
    </div>
  );
}
